import { useWorkspaces } from '../auth/WorkspaceState';
import { useUserState } from '../auth/UserState';
import { WorkspaceMemberRole } from '../auth/workspace';
import { IntegrationType, partners } from '../integrations/partner';

/**
 * Utility function to format workspace member role names for display
 */
export function formatRoleName(role: WorkspaceMemberRole): string {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'content-manager':
      return 'Content Manager';
    case 'transcript-editor':
      return 'Transcript Editor';
    case 'viewer':
      return 'Viewer';
    case 'integration-reviewer':
      return 'Integration Reviewer';
    default:
      return role;
  }
}

/** Checks whether the current workspace role is transcript editor, content manager, or admin */
export function useIsTranscriptEditor(): boolean {
  const { currentRole } = useWorkspaces();
  return (
    currentRole === 'transcript-editor' ||
    currentRole === 'content-manager' ||
    currentRole === 'integration-reviewer' ||
    currentRole === 'admin'
  );
}

/** Checks whether the current workspace role is content manager or admin */
export function useIsContentManager(): boolean {
  const { currentRole } = useWorkspaces();
  return (
    currentRole === 'content-manager' ||
    currentRole === 'integration-reviewer' ||
    currentRole === 'admin'
  );
}

/** Checks whether the current workspace role is admin */
export function useIsAdmin(): boolean {
  const { currentRole } = useWorkspaces();
  return currentRole === 'admin';
}

/** Checks whether the current workspace role is integration reviewer */
export function useIsIntegrationReviewer(): boolean {
  const { currentRole } = useWorkspaces();
  return currentRole === 'integration-reviewer';
}

/**
 * Hook to get integration access scope for integration reviewers
 * Returns allowed integrations and permission checks
 */
export function useIntegrationScope() {
  const { currentRole, current } = useWorkspaces();
  const { authClaims } = useUserState();
  const isIntegrationReviewer = currentRole === 'integration-reviewer';

  // Find the current workspace's claims to get scopedAccess
  const currentWorkspaceClaims = authClaims?.workspaces?.find(
    (claim: any) => claim.id === current?.workspaceId,
  );

  const scopedAccess = currentWorkspaceClaims?.scopedAccess;

  // Parse comma-separated string into array
  const rawAllowedIntegrations: string[] = scopedAccess?.allowedIntegrations
    ? scopedAccess.allowedIntegrations
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean)
    : [];

  // Check if access has expired
  const hasExpired = scopedAccess?.expiresAt && new Date(scopedAccess.expiresAt) < new Date();

  // Filter to only include valid IntegrationType values
  const allowedIntegrations: IntegrationType[] = hasExpired
    ? []
    : rawAllowedIntegrations.filter((integration): integration is IntegrationType =>
        partners.includes(integration as IntegrationType),
      );

  // Parse comma-separated permissions string into array
  const permissions: string[] = hasExpired
    ? []
    : scopedAccess?.permissions
      ? scopedAccess.permissions
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean)
      : [];

  return {
    isIntegrationReviewer,
    // For integration reviewers, return their allowed integrations
    // For other roles, return undefined (no restrictions)
    allowedIntegrations: isIntegrationReviewer ? allowedIntegrations : undefined,

    // Check if user can access a specific integration
    canAccess: (integrationType: IntegrationType): boolean => {
      if (!isIntegrationReviewer) return true; // Non-reviewers have full access
      if (hasExpired) return false; // Expired access
      return allowedIntegrations.includes(integrationType);
    },

    // Check if user has specific permissions
    permissions: isIntegrationReviewer ? permissions : ['full'],

    // Additional fields from scopedAccess
    expiresAt: scopedAccess?.expiresAt,
    grantedBy: scopedAccess?.grantedBy,
    hasExpired,
  };
}
